/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import "./index.css";

import { $isCodeHighlightNode } from "@lexical/code";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $findMatchingParent, $getNearestNodeOfType, mergeRegister } from "@lexical/utils";
import {
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  $isRootOrShadowRoot,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
  $isElementNode,
} from "lexical";
import { Dispatch, useCallback, useEffect, useRef, useState } from "react";
import * as React from "react";
import { createPortal } from "react-dom";
import { $isListNode, ListNode } from "@lexical/list";
import { $isHeadingNode } from "@lexical/rich-text";
import { $patchStyleText, $getSelectionStyleValueForProperty } from "@lexical/selection";

import { getDOMRangeRect } from "../../utils/getDOMRangeRect";
import { getSelectedNode } from "../../utils/getSelectedNode";
import { setFloatingElemPosition } from "../../utils/setFloatingElemPosition";

import { BoldIcon } from "components/NewEditor/components/icons/BoldIcon";
import { ItalicIcon } from "components/NewEditor/components/icons/ItalicIcon";
import { UnderlineIcon } from "components/NewEditor/components/icons/UnderlineIcon";
import { InlineCodeIcon } from "components/NewEditor/components/icons/InlineCodeIcon";
import { InlineLinkIcon } from "components/NewEditor/components/icons/InlineLinkIcon";
import { TextColorIcon } from "components/NewEditor/components/icons/TextColorIcon";
import { FloatingMarkupIcon } from "components/NewEditor/components/icons/FloatingMarkupIcon";
import { Tooltip } from "@slid/slid-ips";
import { BlockFormatDropDown, blockTypeToBlockName } from "../ToolbarPlugin";
import styled from "styled-components";
import { DropdownBgColorPicker, DropdownTextColorPicker } from "components/NewEditor/ui/DropdownColorPicker";
import { useTranslation } from "react-i18next";
import { SHORTCUTS } from "../ShortcutsPlugin/shortcuts";

function TextFormatFloatingToolbar({
  editor,
  anchorElem,
  isLink,
  isBold,
  isItalic,
  isUnderline,
  isCode,
  isStrikethrough,
  isSubscript,
  isSuperscript,
  setIsLinkEditMode,
  blockType,
  bgColor,
  fontColor,
  onBgColorSelect,
  onFontColorSelect,
}: {
  editor: LexicalEditor;
  anchorElem: HTMLElement;
  isBold: boolean;
  isCode: boolean;
  isItalic: boolean;
  isLink: boolean;
  isStrikethrough: boolean;
  isSubscript: boolean;
  isSuperscript: boolean;
  isUnderline: boolean;
  setIsLinkEditMode: Dispatch<boolean>;
  blockType: keyof typeof blockTypeToBlockName;
  bgColor: string;
  fontColor: string;
  onBgColorSelect: (color: string, skipHistoryStack: boolean) => void;
  onFontColorSelect: (color: string, skipHistoryStack: boolean) => void;
}): JSX.Element {
  const popupCharStylesEditorRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation("EditorToolbar");

  const insertLink = useCallback(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, "https://");
      setIsLinkEditMode(true);
    } else {
      setIsLinkEditMode(false);
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink, setIsLinkEditMode]);

  function mouseMoveListener(e: MouseEvent) {
    if (popupCharStylesEditorRef?.current && (e.buttons === 1 || e.buttons === 3)) {
      if (popupCharStylesEditorRef.current.style.pointerEvents !== "none") {
        const x = e.clientX;
        const y = e.clientY;
        const elementUnderMouse = document.elementFromPoint(x, y);

        if (!popupCharStylesEditorRef.current.contains(elementUnderMouse)) {
          // Mouse is not over the target element => not a normal click, but probably a drag
          popupCharStylesEditorRef.current.style.pointerEvents = "none";
        }
      }
    }
  }
  function mouseUpListener(e: MouseEvent) {
    if (popupCharStylesEditorRef?.current) {
      if (popupCharStylesEditorRef.current.style.pointerEvents !== "auto") {
        popupCharStylesEditorRef.current.style.pointerEvents = "auto";
      }
    }
  }

  useEffect(() => {
    if (popupCharStylesEditorRef?.current) {
      document.addEventListener("mousemove", mouseMoveListener);
      document.addEventListener("mouseup", mouseUpListener);

      return () => {
        document.removeEventListener("mousemove", mouseMoveListener);
        document.removeEventListener("mouseup", mouseUpListener);
      };
    }
  }, [popupCharStylesEditorRef]);

  const $updateTextFormatFloatingToolbar = useCallback(() => {
    const selection = $getSelection();

    const popupCharStylesEditorElem = popupCharStylesEditorRef.current;
    const nativeSelection = window.getSelection();

    if (popupCharStylesEditorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();
    if (selection !== null && nativeSelection !== null && !nativeSelection.isCollapsed && rootElement !== null && rootElement.contains(nativeSelection.anchorNode)) {
      const rangeRect = getDOMRangeRect(nativeSelection, rootElement);

      setFloatingElemPosition(rangeRect, popupCharStylesEditorElem, anchorElem, isLink);
    }
  }, [editor, anchorElem, isLink]);

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement;

    const update = () => {
      editor.getEditorState().read(() => {
        $updateTextFormatFloatingToolbar();
      });
    };

    window.addEventListener("resize", update);
    if (scrollerElem) {
      scrollerElem.addEventListener("scroll", update);
    }

    return () => {
      window.removeEventListener("resize", update);
      if (scrollerElem) {
        scrollerElem.removeEventListener("scroll", update);
      }
    };
  }, [editor, $updateTextFormatFloatingToolbar, anchorElem]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      $updateTextFormatFloatingToolbar();
    });
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateTextFormatFloatingToolbar();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          $updateTextFormatFloatingToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor, $updateTextFormatFloatingToolbar]);

  return (
    <div ref={popupCharStylesEditorRef} className="floating-text-format-popup">
      {editor.isEditable() && (
        <>
          <BlockFormatDropDown disabled={false} blockType={blockType} editor={editor} isFloatingToolbar={true} />
          <Divider />
          <Tooltip title={t("bold")} placement="top" description={SHORTCUTS.BOLD}>
            <button
              type="button"
              onClick={() => {
                editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
              }}
              className={"popup-item " + (isBold ? "active" : "")}
              aria-label="Format text as bold"
            >
              <BoldIcon color={isBold ? "var(--blue7)" : "var(--gray17)"} />
            </button>
          </Tooltip>
          <Tooltip title={t("italic")} placement="top" description={SHORTCUTS.ITALIC}>
            <button
              type="button"
              onClick={() => {
                editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
              }}
              className={"popup-item " + (isItalic ? "active" : "")}
              aria-label="Format text as italics"
            >
              <ItalicIcon color={isItalic ? "var(--blue7)" : "var(--gray17)"} />
            </button>
          </Tooltip>
          <Tooltip title={t("underline")} placement="top" description={SHORTCUTS.UNDERLINE}>
            <button
              type="button"
              onClick={() => {
                editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");
              }}
              className={"popup-item " + (isUnderline ? "active" : "")}
              aria-label="Format text to underlined"
            >
              <UnderlineIcon color={isUnderline ? "var(--blue7)" : "var(--gray17)"} />
            </button>
          </Tooltip>
          <DropdownTextColorPicker
            disabled={false}
            buttonClassName="toolbar-item color-picker"
            buttonAriaLabel="Formatting text color"
            buttonIconClassName="icon font-color"
            buttonIcon={<TextColorIcon color={fontColor} />}
            color={fontColor}
            onChange={onFontColorSelect}
            title="text color"
            showChevron={true}
            isFloatingToolbar={true}
          />
          <DropdownBgColorPicker
            disabled={false}
            buttonClassName="toolbar-item color-picker"
            buttonAriaLabel="Formatting background color"
            buttonIconClassName="icon bg-color"
            buttonIcon={<FloatingMarkupIcon color={bgColor} />}
            color={bgColor}
            onChange={onBgColorSelect}
            title="bg color"
            showChevron={true}
            isFloatingToolbar={true}
          />
          <Tooltip title={t("link")} placement="top" description={SHORTCUTS.INLINE_LINK}>
            <button type="button" onClick={insertLink} className={"popup-item " + (isLink ? "active" : "")} aria-label="Insert link">
              <InlineLinkIcon color={isLink ? "var(--blue7)" : "var(--gray17)"} />
            </button>
          </Tooltip>
          <Tooltip title={t("inline-code")} placement="top" description={SHORTCUTS.INLINE_CODE}>
            <button
              type="button"
              onClick={() => {
                editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code");
              }}
              className={"popup-item " + (isCode ? "active" : "")}
              aria-label="Insert code block"
            >
              <InlineCodeIcon color={isCode ? "var(--blue7)" : "var(--gray17)"} />
            </button>
          </Tooltip>
        </>
      )}
    </div>
  );
}

const Divider = styled.div`
  height: 32px;
  width: 1px;
  background-color: var(--gray4);
`;

function useFloatingTextFormatToolbar(editor: LexicalEditor, anchorElem: HTMLElement, setIsLinkEditMode: Dispatch<boolean>): JSX.Element | null {
  const [isText, setIsText] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isSubscript, setIsSubscript] = useState(false);
  const [isSuperscript, setIsSuperscript] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [blockType, setBlockType] = useState<keyof typeof blockTypeToBlockName>("slid-paragraph");
  const [fontColor, setFontColor] = useState<string>("#000000");
  const [bgColor, setBgColor] = useState<string>("#fff");

  const updatePopup = useCallback(() => {
    editor.getEditorState().read(() => {
      // Should not to pop up the floating toolbar when using IME input
      if (editor.isComposing()) {
        return;
      }

      const selection = $getSelection();
      const nativeSelection = window.getSelection();
      const rootElement = editor.getRootElement();

      if (nativeSelection !== null && (!$isRangeSelection(selection) || rootElement === null || !rootElement.contains(nativeSelection.anchorNode))) {
        setIsText(false);
        return;
      }

      if (!$isRangeSelection(selection)) {
        return;
      }

      const anchorNode = selection.anchor.getNode();
      let element =
        anchorNode.getKey() === "root"
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
              const parent = e.getParent();
              return parent !== null && $isRootOrShadowRoot(parent);
            });

      const node = getSelectedNode(selection);
      const elementKey = node.getKey();
      const elementDOM = editor.getElementByKey(elementKey);

      // Update text format
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsUnderline(selection.hasFormat("underline"));
      setIsStrikethrough(selection.hasFormat("strikethrough"));
      setIsSubscript(selection.hasFormat("subscript"));
      setIsSuperscript(selection.hasFormat("superscript"));
      setIsCode(selection.hasFormat("code"));

      // Update links
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }

      if (!$isCodeHighlightNode(selection.anchor.getNode()) && selection.getTextContent() !== "") {
        setIsText($isTextNode(node) || $isParagraphNode(node));
      } else {
        setIsText(false);
      }

      const rawTextContent = selection.getTextContent().replace(/\n/g, "");
      if (!selection.isCollapsed() && rawTextContent === "") {
        setIsText(false);
        return;
      }

      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
          const type = parentList ? parentList.getListType() : element.getListType();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element) ? element.getTag() : element?.getType();
          if (type && type in blockTypeToBlockName) {
            setBlockType(type as keyof typeof blockTypeToBlockName);
          }
        }
      }

      // Handle buttons
      setFontColor($getSelectionStyleValueForProperty(selection, "color") || "#000000");
      setBgColor($getSelectionStyleValueForProperty(selection, "background-color") || "#fff");
      // setFontFamily($getSelectionStyleValueForProperty(selection, "font-family", "Arial"));
      let matchingParent;
      if ($isLinkNode(parent)) {
        // If node is a link, we need to fetch the parent paragraph node to set format
        matchingParent = $findMatchingParent(node, (parentNode) => $isElementNode(parentNode) && !parentNode.isInline());
      }
    });
  }, [editor]);

  const applyStyleText = useCallback(
    (styles: Record<string, string>, skipHistoryStack?: boolean) => {
      editor.update(
        () => {
          const selection = $getSelection();
          if (selection !== null) {
            $patchStyleText(selection, styles);
          }
        },
        skipHistoryStack ? { tag: "historic" } : {}
      );
    },
    [editor]
  );

  const onFontColorSelect = useCallback(
    (value: string, skipHistoryStack: boolean) => {
      applyStyleText({ color: value }, skipHistoryStack);
    },
    [applyStyleText]
  );

  const onBgColorSelect = useCallback(
    (value: string, skipHistoryStack: boolean) => {
      applyStyleText({ "background-color": value }, skipHistoryStack);
    },
    [applyStyleText]
  );

  useEffect(() => {
    document.addEventListener("selectionchange", updatePopup);
    return () => {
      document.removeEventListener("selectionchange", updatePopup);
    };
  }, [updatePopup]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(() => {
        updatePopup();
      }),
      editor.registerRootListener(() => {
        if (editor.getRootElement() === null) {
          setIsText(false);
        }
      })
    );
  }, [editor, updatePopup]);

  if (!isText) {
    return null;
  }

  return createPortal(
    <TextFormatFloatingToolbar
      editor={editor}
      anchorElem={anchorElem}
      isLink={isLink}
      isBold={isBold}
      isItalic={isItalic}
      isStrikethrough={isStrikethrough}
      isSubscript={isSubscript}
      isSuperscript={isSuperscript}
      isUnderline={isUnderline}
      isCode={isCode}
      setIsLinkEditMode={setIsLinkEditMode}
      blockType={blockType}
      bgColor={bgColor}
      fontColor={fontColor}
      onBgColorSelect={onBgColorSelect}
      onFontColorSelect={onFontColorSelect}
    />,
    anchorElem
  );
}

export default function FloatingTextFormatToolbarPlugin({ anchorElem = document.body, setIsLinkEditMode }: { anchorElem?: HTMLElement; setIsLinkEditMode: Dispatch<boolean> }): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  return useFloatingTextFormatToolbar(editor, anchorElem, setIsLinkEditMode);
}
